<template>
  <v-layout>
    <template v-if="authToken">
      <s-global-navigation
        :token="authToken"
        :host="globalNavigationHost"
        :environment="environment"
        :local-navigation-menu-list="localNavigationMenuItems"
        :local-navigation-menu-default-index="localNavigationMenuDefaultIndex"
        :global-navigation-title="globalNavigationTitle"
        :local-navigation-title="localNavigationTitle"
        :icon="customAppIcon"
        :is-organization-switching-enabled="isEnableOrganizationSwitching"
        has-dark-mode
        :has-settings="true"
        @click:settings="onClickSettings"
        @click:logout="onClickLogout"
      >
        <v-main>
          <v-container class="main-container pa-3">
            <slot v-if="!error.redirect" />
            <error-dialog-component
              :is-visible="error.showDialog || false"
              @close="closeErrorDialog"
              @redirectClose="redirect"
            />
          </v-container>
        </v-main>
      </s-global-navigation>
    </template>
    <template v-else>
      <loading class="loading" />
    </template>

    <v-snackbar
      v-model="notificationModelValue"
      :color="notification?.color ?? 'success'"
      :timeout="notification?.timeout ?? 2000"
      :location="notification?.location ?? 'top'"
      :text="notification?.message ?? ''"
      content-class="text-pre-wrap"
    >
      <template v-slot:actions>
        <v-btn @click="onNotificationAction">
          {{ notification?.actionText ?? t('button.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <confirm-dialog
      v-if="confirmDialogParam"
      :title="confirmDialogParam.title"
      :message="confirmDialogParam.message"
      :positive-button-color="confirmDialogParam.positiveButtonColor"
      :positive-button-label="confirmDialogParam.positiveButtonLabel"
      :negative-button-label="confirmDialogParam.negativeButtonLabel"
      @close="confirmDialogParam.onClose"
    />
  </v-layout>
</template>

<script setup lang="ts">
import { useAuth0 } from '@auth0/auth0-vue';
import { useAsyncData, useRoute } from 'nuxt/app';
import loading from '@/components/common/loading.vue';
import ErrorDialogComponent from '@/components/common/error-dialog-component.vue';
import ConfirmDialog from '@/components/common/confirm-dialog.vue';
import { useErrorStore } from '@/stores/index';
import type { Error as ErrorStore } from '@/types/models/error.d';
import {
  computed,
  watch,
  navigateTo,
  onUnmounted,
  useRuntimeConfig,
  useRouter,
  useApiClient,
  useErrorHandler,
  useI18n,
  useHead,
} from '#imports';
import { type ComputedRef, ref, watchEffect } from 'vue';
import { AUTH, PAGE } from '@/constants/common';
import PAGE_URLS from '@/constants/common/page';
import type { LocalNavigationMenuListItem } from '@sensyn-robotics/sensyn-ui-vue3';
import { useCustomText } from '@/composables/use-custom-text';
import { useNotification } from '@/composables/use-notification';
import { tryRecoverSession } from '@/utils/auth/session-recovery';
import { useConfirmDialog } from '@/composables/use-confirm-dialog';

const { t, mergeLocaleMessage } = useI18n();
const { getAccessTokenSilently } = useAuth0();
const errorStore = useErrorStore();
const router = useRouter();
const datastoreClient = await useApiClient().useDatastoreClient();
const { handleError } = useErrorHandler();
const { fetchCustomText, getCustomAppTitle, getCustomAppLogo, getOtherTexts, getLang } =
  useCustomText();
const { notification, notificationModelValue, closeNotify } = useNotification();
const { confirmDialogParam } = useConfirmDialog();

const authToken = ref('');

// エラー
const error: ComputedRef<ErrorStore> = computed(() => errorStore.error);

// グローバルナビゲーションのホスト
const globalNavigationHost: ComputedRef<string> = computed(() => {
  const url = new URL(location.href);
  return url.host.includes('personal') || url.host.includes('local')
    ? url.origin.replace('datastore.', '')
    : useRuntimeConfig().public.globalNavigationHostname;
});

const routeName = () => {
  const route = useRoute();
  return route.name ?? t('common.undefined');
};

const globalNavigationTitle: ComputedRef<string> = computed(() => {
  switch (routeName()) {
    case 'upload':
      return t('header.page.file-upload');
    case 'files':
      return t('header.page.file-list');
    case 'files-detail':
      return t('header.page.file-detail');
    case 'file-group-detail':
      return t('header.page.file-group-detail');
    case 'settings':
      return t('header.page.settings');
    case 'workflow-runs':
      return t('header.page.workflow-runs');
    default:
      return t('common.undefined');
  }
});

// ローカルナビゲーションタイトル（カスタムアプリ名対応）
const localNavigationTitle = computed(() => getCustomAppTitle() ?? t('menu.datastore'));

// キャッシュからの即時i18n適用（FOUC防止）
const cachedOtherTexts = getOtherTexts();
if (Object.keys(cachedOtherTexts).length > 0) {
  mergeLocaleMessage(getLang(), cachedOtherTexts);
}

// ヘッダーロゴ（カスタムロゴ対応、未設定時はundefinedでデフォルトアイコンを使用）
const customAppIcon = computed(() => getCustomAppLogo() ?? undefined);

// ブラウザタイトル・favicon（カスタム対応）
const DEFAULT_TITLE = 'SENSYN CORE Datastore';
const DEFAULT_FAVICON = '/images/favicon/favicon.svg';
useHead({
  title: computed(() => getCustomAppTitle() ?? DEFAULT_TITLE),
  link: computed(() => {
    const customLogo = getCustomAppLogo();
    if (customLogo) {
      return [
        { key: 'shortcut-icon', rel: 'shortcut icon', href: customLogo },
        { key: 'icon', rel: 'icon', href: customLogo },
      ];
    }
    return [
      { key: 'shortcut-icon', rel: 'shortcut icon', type: 'image/svg+xml', href: DEFAULT_FAVICON },
      { key: 'icon', rel: 'icon', type: 'image/svg+xml', href: DEFAULT_FAVICON },
    ];
  }),
});

// 環境
const environment: ComputedRef<string> = computed(() => {
  const url = new URL(location.href);
  if (url.host.includes('local')) {
    return 'local';
  } else if (url.host.includes('dev')) {
    return 'dev';
  } else if (url.host.includes('stg')) {
    return 'stg';
  } else {
    return 'prod';
  }
});

// ローカルナビゲーションメニュー項目
const localNavigationMenuItems: ComputedRef<Array<LocalNavigationMenuListItem>> = computed(() => [
  {
    name: t('header.page.file-list'),
    icon: 'mdi-format-list-bulleted-square',
    onClick: onClickFileListMenuItem,
  },
  {
    name: t('header.page.file-upload'),
    icon: 'mdi-cloud-upload-outline',
    onClick: onClickUploadMenuItem,
  },
  {
    name: t('header.page.workflow-runs'),
    icon: 'mdi-history',
    onClick: onClickWorkflowRunsMenuItem,
  },
]);

const localNavigationMenuDefaultIndex: ComputedRef<number> = computed(() => {
  switch (routeName()) {
    case 'files':
    case 'files-detail':
    case 'file-group-detail':
      return 0;
    case 'upload':
      return 1;
    case 'workflow-runs':
      return 2;
    default:
      return -1;
  }
});

const { data: _me, error: meError } = useAsyncData('userMe', async () => {
  const res = await (await datastoreClient).useUserApi().getUserMe();
  return res.data;
});

const isEnableOrganizationSwitching = computed(() => {
  // NOTE: 本来は effective-role-permission による判定が必要だと思うが、
  //       /users/me をとってくるまでの間に false になってグロナビ側で cookie の読み込みがされず、
  //       組織切り替え時の cookie 登録イベントが発行されていなさそうだった
  //       mapping-tools では true をそのまま set していたのでここもそのまま true を返す
  // const effectiveRolePermission = _me.value?.effective_role_permissions ?? [];
  // return !!effectiveRolePermission.length;
  return true;
});

watch(
  error,
  (newError: ErrorStore) => {
    if (newError.showDialog) {
      openErrorDialog();
    }
  },
  { immediate: true, deep: true },
);

watch(meError, () => {
  const e = meError.value;
  if (e && e instanceof Error) {
    handleError(e);
  }
});

// この watchEffect は setup 時の一度しか走らない。
// 依存として登録されるのは最初の await より前に読んだ reactive 値だけで、
// ここでは `getAccessTokenSilently()` を呼ぶだけなので依存が 0 個になる。
// したがって catch へ来た時点の authToken は必ず初期値の空文字であり、
// 「トークンを一度も取れていない」= ログアウトへ送る、の一択になる。
//
// ただしこれは @auth0/auth0-vue の内部実装に依存した性質である。
// v2.9.0 では getAccessTokenSilently が __proxy(cb) を呼び、その本体が
// `await cb()` から始まるため await 前の reactive 読み取りが無い、という
// 実装を確認したうえでの前提にすぎない。上流が await より前に reactive な
// 読み取りを 1 つでも入れると、この watchEffect は依存を持って再実行される
// ようになり、「トークン取得済みで後続のリフレッシュに失敗」した場合に
// ログアウトさせてしまう (本来はセッションを保ってエラー表示すべき場面)。
//
// tests/layouts/default.test.ts はこの前提を「一度しか走らない」形で
// 固定しているが、@auth0/auth0-vue 自体をモックしているため、上流が
// 前提を壊してもテストは緑のまま通る点に注意。Auth0 を上げるときは
// この箇所を実物で確認すること。
watchEffect(async () => {
  try {
    const token = await getAccessTokenSilently();
    authToken.value = token || '';
  } catch {
    tryRecoverSession(() => navigateTo(AUTH.REDIRECT.LOGOUT));
  }
});

// カスタムテキスト取得（認証トークン取得後に実行）
watch(
  authToken,
  async (token) => {
    if (!token) return;
    await fetchCustomText();
    const otherTexts = getOtherTexts();
    if (Object.keys(otherTexts).length > 0) {
      mergeLocaleMessage(getLang(), otherTexts);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  errorStore.reset();
});

const onClickFileListMenuItem = () => {
  // Navigate to /files/
  router.push('/files/');
};

const onClickUploadMenuItem = () => {
  // Navigate to /upload/
  router.push('/upload/');
};

const onClickWorkflowRunsMenuItem = () => {
  // Navigate to /workflow-runs/
  router.push(PAGE_URLS.WORKFLOW_RUNS);
};

const onClickLogout = () => {
  router.push('/auth/logout/?slo=true');
};

const openErrorDialog = () => {
  errorStore.update({ showDialog: true });
};

const closeErrorDialog = async () => {
  const navigatePath = error.value.closeNavigateTo;
  errorStore.reset();
  if (navigatePath) {
    await navigateTo(navigatePath);
  }
};

const redirect = () => {
  errorStore.reset();
  navigateTo(AUTH.REDIRECT.LOGOUT);
};

const onNotificationAction = () => {
  notification.value?.onAction?.();
  closeNotify();
};

const onClickSettings = () => {
  navigateTo(PAGE.SETTINGS);
};
</script>

<style lang="scss" scoped>
.main-container {
  width: 100% !important;
  max-width: 100% !important;
  height: calc(100dvh - 60px);
  padding: 0 !important;
  background-color: var(--v-color-background-base);
}

.setting-menu {
  position: absolute;
  top: 48px;
  right: 128px;
  z-index: 1000;
  width: 384px;
  padding: 16px 0;
  border-radius: 8px;
  box-shadow:
    0 1px 8px 0 rgb(13 14 15 / 7%),
    0 2px 12px 0 rgb(13 14 15 / 7%),
    0 -2px 8px 0 rgb(13 14 15 / 5%);
}

.loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
